import { AxiosResponse } from 'axios'
import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import RmaDao, { RmaReturnConditionDao } from '@/interfaces/rma/Rma.dao'
import { RmaCreateDto, RmaUpdateDto } from '@/interfaces/rma/RmaWrite.dto'
import {
    RmaExpectedLinesCreateDto,
    RmaExpectedLinesFromMaterialsDto,
    RmaReceiptLineUpdateDto,
    RmaReceivedLinesCreateDto,
    RmaReceivedLinesResultDao,
} from '@/interfaces/rma/RmaReceiptWrite.dto'
import RmaOrderLineDao from '@/interfaces/rma/RmaOrderLine.dao'
import {
    RmaAddNotesDto,
    RmaReplacementOrderByOrderLinesDto,
    RmaReplacementOrderResultDao,
    RmaReplacementOrderValidateDto,
    RmaReplacementOrderValidationDao,
} from '@/interfaces/rma/RmaReplacementOrder.dto'
import RmaMaterialSearchDao from '@/interfaces/rma/RmaMaterialSearch.dao'
import { RmaChannelAccountDao, RmaOperationsCentreDao, RmaPaymentMethodDao } from '@/interfaces/rma/RmaLookups.dao'
import RmaReasonCodeDao from '@/interfaces/rma/RmaReasonCode.dao'
import RmaSearchSummaryDao from '@/interfaces/rma/RmaSearchSummary.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'

export default class RmaService extends ApiService {
    async getRmas(params?: IRequestParameters): Promise<[RmaSearchSummaryDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RmaSearchSummaryDao[], PageNumberPagination] | null>(
            'GET',
            '/rma',
            params,
            undefined,
            undefined,
            'getRmas',
        ).catch(() => null)
    }

    async getRma(psoqNumber: string): Promise<RmaDao | null> {
        return this.request<undefined, RmaDao>(
            'GET',
            `/rma/${encodeURIComponent(psoqNumber)}`,
            undefined,
            undefined,
            undefined,
            'getRma',
        ).catch(() => null)
    }

    async getRmaReasonCodes(): Promise<RmaReasonCodeDao[] | null> {
        return this.request<undefined, RmaReasonCodeDao[]>(
            'GET',
            '/rma/reason-codes',
            undefined,
            undefined,
            undefined,
            'getRmaReasonCodes',
        ).catch(() => null)
    }

    async getRmaPaymentMethods(): Promise<RmaPaymentMethodDao[] | null> {
        return this.request<undefined, RmaPaymentMethodDao[]>(
            'GET',
            '/rma/payment-methods',
            undefined,
            undefined,
            undefined,
            'getRmaPaymentMethods',
        ).catch(() => null)
    }

    async getRmaChannelAccounts(): Promise<RmaChannelAccountDao[] | null> {
        return this.request<undefined, RmaChannelAccountDao[]>(
            'GET',
            '/rma/channel-accounts',
            undefined,
            undefined,
            undefined,
            'getRmaChannelAccounts',
        ).catch(() => null)
    }

    async getRmaOperationsCentres(): Promise<RmaOperationsCentreDao[] | null> {
        return this.request<undefined, RmaOperationsCentreDao[]>(
            'GET',
            '/rma/operations-centres',
            undefined,
            undefined,
            undefined,
            'getRmaOperationsCentres',
        ).catch(() => null)
    }

    async createRma(rmaCreateDto: RmaCreateDto): Promise<RmaDao | null> {
        return this.request<RmaCreateDto, RmaDao>(
            'POST',
            '/rma',
            undefined,
            rmaCreateDto,
            undefined,
            'createRma',
        ).catch(() => null)
    }

    async updateRma(psoqNumber: string, rmaUpdateDto: RmaUpdateDto): Promise<RmaDao | null> {
        return this.request<RmaUpdateDto, RmaDao>(
            'PUT',
            `/rma/${encodeURIComponent(psoqNumber)}`,
            undefined,
            rmaUpdateDto,
            undefined,
            'updateRma',
        ).catch(() => null)
    }

    async closeRma(psoqNumber: string): Promise<RmaDao | null> {
        return this.request<undefined, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/close`,
            undefined,
            undefined,
            undefined,
            'closeRma',
        ).catch(() => null)
    }

    async reopenRma(psoqNumber: string): Promise<RmaDao | null> {
        return this.request<undefined, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/reopen`,
            undefined,
            undefined,
            undefined,
            'reopenRma',
        ).catch(() => null)
    }

    async archiveRma(psoqNumber: string, rmaUpdateDto: RmaUpdateDto): Promise<RmaDao | null> {
        return this.request<RmaUpdateDto, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/archive`,
            undefined,
            rmaUpdateDto,
            undefined,
            'archiveRma',
        ).catch(() => null)
    }

    async releaseRmaForExport(psoqNumber: string): Promise<RmaDao | null> {
        return this.request<undefined, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/release-for-export`,
            undefined,
            undefined,
            undefined,
            'releaseRmaForExport',
        ).catch(() => null)
    }

    async updateRmaReturnRequired(psoqNumber: string, value: boolean): Promise<RmaDao | null> {
        return this.request<{ value: boolean }, RmaDao>(
            'PUT',
            `/rma/${encodeURIComponent(psoqNumber)}/return-required`,
            undefined,
            { value },
            undefined,
            'updateRmaReturnRequired',
        ).catch(() => null)
    }

    async updateRmaCreditNoteAuthorisation(psoqNumber: string, value: boolean): Promise<RmaDao | null> {
        return this.request<{ value: boolean }, RmaDao>(
            'PUT',
            `/rma/${encodeURIComponent(psoqNumber)}/credit-note-authorisation`,
            undefined,
            { value },
            undefined,
            'updateRmaCreditNoteAuthorisation',
        ).catch(() => null)
    }

    async getRmaOrderLines(psoqNumber: string): Promise<RmaOrderLineDao[] | null> {
        return this.request<undefined, RmaOrderLineDao[]>(
            'GET',
            `/rma/${encodeURIComponent(psoqNumber)}/order-lines`,
            undefined,
            undefined,
            undefined,
            'getRmaOrderLines',
        ).catch(() => null)
    }

    async getRmaReturnConditionCodes(): Promise<RmaReturnConditionDao[] | null> {
        return this.request<undefined, RmaReturnConditionDao[]>(
            'GET',
            '/rma/return-condition-codes',
            undefined,
            undefined,
            undefined,
            'getRmaReturnConditionCodes',
        ).catch(() => null)
    }

    async searchRmaMaterials(
        params: IRequestParameters,
    ): Promise<[RmaMaterialSearchDao[], PageNumberPagination] | null> {
        return this.request<undefined, [RmaMaterialSearchDao[], PageNumberPagination]>(
            'GET',
            '/rma/material-search',
            params,
            undefined,
            undefined,
            'searchRmaMaterials',
        ).catch(() => null)
    }

    async addRmaExpectedLines(psoqNumber: string, dto: RmaExpectedLinesCreateDto): Promise<RmaDao | null> {
        return this.request<RmaExpectedLinesCreateDto, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/receipt/expected-lines`,
            undefined,
            dto,
            undefined,
            'addRmaExpectedLines',
        ).catch(() => null)
    }

    async addRmaExpectedLinesFromMaterials(
        psoqNumber: string,
        dto: RmaExpectedLinesFromMaterialsDto,
    ): Promise<RmaDao | null> {
        return this.request<RmaExpectedLinesFromMaterialsDto, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/receipt/expected-lines/from-materials`,
            undefined,
            dto,
            undefined,
            'addRmaExpectedLinesFromMaterials',
        ).catch(() => null)
    }

    async addRmaReceivedLines(
        psoqNumber: string,
        dto: RmaReceivedLinesCreateDto,
    ): Promise<RmaReceivedLinesResultDao | null> {
        return this.request<RmaReceivedLinesCreateDto, RmaReceivedLinesResultDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/receipt/received-lines`,
            undefined,
            dto,
            undefined,
            'addRmaReceivedLines',
        ).catch(() => null)
    }

    async updateRmaReceiptLine(psoqNumber: string, id: number, dto: RmaReceiptLineUpdateDto): Promise<RmaDao | null> {
        return this.request<RmaReceiptLineUpdateDto, RmaDao>(
            'PUT',
            `/rma/${encodeURIComponent(psoqNumber)}/receipt/received-lines/${id}`,
            undefined,
            dto,
            undefined,
            'updateRmaReceiptLine',
        ).catch(() => null)
    }

    async deleteRmaReceiptLine(psoqNumber: string, id: number): Promise<RmaDao | null> {
        return this.request<undefined, RmaDao>(
            'DELETE',
            `/rma/${encodeURIComponent(psoqNumber)}/receipt/lines/${id}`,
            undefined,
            undefined,
            undefined,
            'deleteRmaReceiptLine',
        ).catch(() => null)
    }

    async addRmaNotes(psoqNumber: string, dto: RmaAddNotesDto): Promise<RmaDao | null> {
        return this.request<RmaAddNotesDto, RmaDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/notes`,
            undefined,
            dto,
            undefined,
            'addRmaNotes',
        ).catch(() => null)
    }

    async validateRmaReplacementOrder(
        psoqNumber: string,
        dto: RmaReplacementOrderValidateDto,
    ): Promise<RmaReplacementOrderValidationDao | null> {
        return this.request<RmaReplacementOrderValidateDto, RmaReplacementOrderValidationDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/replacement-order/validate`,
            undefined,
            dto,
            undefined,
            'validateRmaReplacementOrder',
        ).catch(() => null)
    }

    async createRmaReplacementOrderByReceivedLines(psoqNumber: string): Promise<RmaReplacementOrderResultDao | null> {
        return this.request<undefined, RmaReplacementOrderResultDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/replacement-order/by-received-lines`,
            undefined,
            undefined,
            undefined,
            'createRmaReplacementOrderByReceivedLines',
        ).catch(() => null)
    }

    async createRmaReplacementOrderByOrderLines(
        psoqNumber: string,
        dto: RmaReplacementOrderByOrderLinesDto,
    ): Promise<RmaReplacementOrderResultDao | null> {
        return this.request<RmaReplacementOrderByOrderLinesDto, RmaReplacementOrderResultDao>(
            'POST',
            `/rma/${encodeURIComponent(psoqNumber)}/replacement-order/by-order-lines`,
            undefined,
            dto,
            undefined,
            'createRmaReplacementOrderByOrderLines',
        ).catch(() => null)
    }

    async exportRmas(params: IRequestParameters): Promise<AxiosResponse | null> {
        return this.request<undefined, AxiosResponse>(
            'POST',
            '/rma/export-rmas',
            params,
            undefined,
            'blob',
            'exportRmas',
        ).catch(() => null)
    }
}
